/**
 * Display currency — presentation only. Redmine #166532.
 * Spec: docs/display-currency-and-destination.md
 *
 * Two separate concepts, and the separation is the whole point:
 *
 *   CHARGE currency  — from the cart's region. What the card is debited in.
 *                      Decides tax, shipping, payment providers, order, refund.
 *   DISPLAY currency — the customer's own choice. Pixels only.
 *
 * Nothing here ever reaches Medusa. There is deliberately no response transform:
 * an earlier revision rewrote amounts inside the SDK response, which destroyed the
 * native figure and made it impossible to show both. Conversion happens at render
 * time, from the native amount, so the charge currency is always still available.
 *
 * `?currency=XXX` keeps its existing meaning — it selects the REGION, which is the
 * Google Feed contract. Display currency is a separate, customer-initiated choice
 * (`?display=XXX` / the `_display_currency` cookie), so a feed landing page always
 * shows the native price and stays in parity with the feed.
 *
 * Edge-safe: no Node APIs, so the middleware can import it.
 */

/** Charge currency of the base catalogue. Rates are quoted against this. */
export const BASE_CURRENCY = "gbp"

export const DISPLAY_PARAM = "display"
export const DISPLAY_CURRENCY_COOKIE = "_display_currency"
export const DISPLAY_CURRENCY_HEADER = "x-display-currency"
/** 30 days, matching the region cookie. */
export const DISPLAY_CURRENCY_MAX_AGE = 60 * 60 * 24 * 30

/**
 * DEMO RATES — 1 GBP = n target.
 *
 * HLA row 4 replaces this with the UKSS `currencies` snapshot, read through a
 * cached store endpoint. The existing UKSS cron already applies the 2% commercial
 * margin, so it must NOT be applied again here or there.
 */
/**
 * Rates are supplied by the caller, never hardcoded here.
 *
 * They come from GET /store/currency-rates, which serves the same snapshot the
 * price sync used to build the real stored prices — so an indicative figure and
 * the price actually charged are computed from identical numbers and cannot
 * disagree. The literal table that used to live here drifted 0.45% from reality
 * within a fortnight, with nobody watching.
 *
 * Keyed by lowercase currency code, values are decimal strings as stored
 * ("1.371492"), including the base currency at "1".
 */
export type DisplayRates = Record<string, string>

/** Currencies with no minor unit. */
const ZERO_DECIMAL = new Set(["jpy"])

export type DisplayCurrencyOption = { code: string; label: string }

/** Names are display-only; the code is what travels. */
const NAMES: Record<string, string> = {
  gbp: "British Pound",
  usd: "US Dollar",
  eur: "Euro",
  chf: "Swiss Franc",
  sgd: "Singapore Dollar",
  cad: "Canadian Dollar",
  aud: "Australian Dollar",
  nzd: "New Zealand Dollar",
  aed: "UAE Dirham",
  pln: "Polish Zloty",
  myr: "Malaysian Ringgit",
  dkk: "Danish Krone",
  hkd: "Hong Kong Dollar",
  nok: "Norwegian Krone",
  sek: "Swedish Krona",
  mxn: "Mexican Peso",
  inr: "Indian Rupee",
  jpy: "Japanese Yen",
}

/**
 * Which currencies the selector offers.
 *
 * Driven by NAMES rather than by whichever rates happen to be loaded: the list a
 * customer sees is a product decision and must not flicker because a rate was
 * briefly unavailable. If a rate is genuinely missing at render time, conversion
 * returns null for that currency and the page shows the native price alone.
 */
export function displayCurrencyOptions(): DisplayCurrencyOption[] {
  return Object.keys(NAMES)
    .map((code) => ({ code, label: NAMES[code] ?? code.toUpperCase() }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function isSupportedDisplayCurrency(code?: string | null): boolean {
  return Boolean(code && NAMES[code.trim().toLowerCase()] !== undefined)
}

/** Lowercase ISO-3 if supported, else null. Accepts the base currency. */
export function normaliseDisplayCurrency(code?: string | null): string | null {
  if (!code) return null
  const wanted = code.trim().toLowerCase()
  return NAMES[wanted] !== undefined ? wanted : null
}

/**
 * Converts a native amount into the display currency.
 *
 * Returns null when there is nothing meaningful to show — same currency, unknown
 * rate, or a missing amount — so callers render the native price alone rather than
 * a duplicate or a wrong figure.
 */
export function toDisplayAmount(
  amount: number | null | undefined,
  chargeCurrency: string | null | undefined,
  displayCurrency: string | null | undefined,
  rates: DisplayRates | null | undefined
): number | null {
  if (amount == null || !Number.isFinite(amount)) return null
  if (!chargeCurrency || !displayCurrency) return null
  // No rates loaded means DO NOT CONVERT. Failing closed is deliberate: showing
  // the native price alone is always correct, whereas converting with a guessed
  // or stale rate quietly misinforms the customer.
  if (!rates) return null

  const from = chargeCurrency.trim().toLowerCase()
  const to = displayCurrency.trim().toLowerCase()
  if (from === to) return null

  /**
   * Parsed to float only here, at the very end. These figures are indicative and
   * are never charged, so float precision is adequate — unlike the sync, where
   * the same shortcut would put a rounding error into real money.
   */
  const fromRate = Number(rates[from])
  const toRate = Number(rates[to])
  if (!Number.isFinite(fromRate) || !Number.isFinite(toRate) || !fromRate || !toRate) {
    return null
  }

  // Via the base currency, so a non-GBP charge currency converts correctly too.
  const raw = (amount / fromRate) * toRate
  return ZERO_DECIMAL.has(to) ? Math.round(raw) : Math.round(raw * 100) / 100
}

/** The rate actually applied, for the "1 GBP = x" disclosure. */
export function displayRate(
  chargeCurrency: string | null | undefined,
  displayCurrency: string | null | undefined,
  rates: DisplayRates | null | undefined
): number | null {
  return toDisplayAmount(1, chargeCurrency, displayCurrency, rates)
}
